import {
  Alert,
  Box,
  IconButton,
  List,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { ReactNode } from 'react';
import { ListGangsResponse } from '../../api/gangsApi';
import { GangCreationStep } from '../../types/gangCreationTypes';
import { GangMemberListItem } from '../molecules/GangMemberListItem';

type GangMember = ListGangsResponse['gangs'][number]['members'][number];

interface GangMemberListSectionProps {
  members: GangMember[];
  itemPrefix: string;
  title: string;
  titleIcon?: ReactNode;
  emptyMessage: string;
  avatarColor: (theme: any) => { bgcolor: string; color: string };
  formatCurrency: (amount: number) => string;
  onEdit?: (step: GangCreationStep) => void;
  editStep: GangCreationStep;
  editTooltip: string;
  testIdPrefix?: string;
  testIdSuffix?: string;
}

export const GangMemberListSection = ({
  members,
  itemPrefix,
  title,
  titleIcon,
  emptyMessage,
  avatarColor,
  formatCurrency,
  onEdit,
  editStep,
  editTooltip,
  testIdPrefix,
  testIdSuffix,
}: GangMemberListSectionProps) => {
  const hasMembers = members.length > 0;
  const buildTestId = (base: string) =>
    `${testIdPrefix ?? ''}${base}${testIdSuffix ?? ''}`;

  return (
    <Box data-testid={buildTestId('section')} sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          data-testid={buildTestId('title')}
          sx={{
            display: titleIcon ? 'flex' : 'block',
            alignItems: 'center',
            gap: titleIcon ? 1 : 0,
          }}
        >
          {titleIcon}
          {title}
        </Typography>
        {onEdit ? (
          <Tooltip title={editTooltip}>
            <IconButton
              size="small"
              onClick={() => onEdit(editStep)}
              data-testid={buildTestId('edit-button')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      {hasMembers ? (
        <List dense data-testid={buildTestId('list')}>
          {members.map(member => (
            <GangMemberListItem
              key={member.userId}
              member={member}
              itemPrefix={itemPrefix}
              avatarColor={avatarColor}
              formatCurrency={formatCurrency}
            />
          ))}
        </List>
      ) : (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mt: 1 }}
          data-testid={buildTestId('empty-message')}
        >
          {emptyMessage}
        </Alert>
      )}
    </Box>
  );
};
